/**
 * Combines multiple class names into a single string, using `clsx` and `tailwind-merge`
 * to handle conditional classes and Tailwind CSS conflicts.
 *
 * @param inputs - An array of class names or conditional class objects.
 * @returns A string containing the merged class names.
 */
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a date string into a human-readable date string.
 *
 * @param dateString - The date string to format.
 * @returns A formatted date string in the format "Weekday, Month Day, Year".
 */
export function formatDate(dateString: string): string {
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  return new Date(dateString).toLocaleDateString("en-US", options);
}

/**
 * Formats a Date object into a human-readable time string.
 *
 * @param date - The Date object to format.
 * @returns A formatted time string in the format "HH:MM AM/PM".
 */
export function formatTime(date: Date): string {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes.toString().padStart(2, "0")} ${period}`;
}
